import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Badge, Button, Col, Form, Nav, Row, Spinner, Table } from 'react-bootstrap';
import { CREATE_VERSION_CONFIG, DELETE_VERSION_CONFIG, GET_ALL_APP_VERSION_CONFIGS, UPDATE_VERSION_CONFIG } from './graphql';
import {
  AppVersionConfig,
  CreateAppVersionConfigMutation,
  CreateAppVersionConfigMutationVariables,
  DeleteAppVersionConfigMutation,
  DeleteAppVersionConfigMutationVariables,
  GetAppVersionConfigsQuery,
  UpdateAppVersionConfigMutation,
  UpdateAppVersionConfigMutationVariables,
} from '../../../../../__gqltypes__';
import CreateVersionConfigModal from './CreateVersionConfigModal';
import DeleteVersionConfigModal from './DeleteVersionConfigModal';
import EditVersionConfigModal from './components/EditVersionConfigModal';

const VersionControl = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState<AppVersionConfig | null>(null);
  const [showEditModal, setShowEditModal] = useState<AppVersionConfig | null>(null);
  const [configs, setConfigs] = useState<AppVersionConfig[]>([]);
  const { data, loading } = useQuery<GetAppVersionConfigsQuery>(GET_ALL_APP_VERSION_CONFIGS, {
    fetchPolicy: 'network-only',
  });
  const [createConfig, { loading: createLoading }] = useMutation<
    CreateAppVersionConfigMutation,
    CreateAppVersionConfigMutationVariables
  >(CREATE_VERSION_CONFIG);
  const [deleteConfig] = useMutation<DeleteAppVersionConfigMutation, DeleteAppVersionConfigMutationVariables>(
    DELETE_VERSION_CONFIG
  );
  const [updateConfig, { loading: updateLoading }] = useMutation<
    UpdateAppVersionConfigMutation,
    UpdateAppVersionConfigMutationVariables
  >(UPDATE_VERSION_CONFIG, {
    update(cache, { data: updateData }) {
      if (!updateData?.updateAppVersionConfig) {
        return;
      }
      cache.writeQuery({
        query: GET_ALL_APP_VERSION_CONFIGS,
        data: { getAppVersionConfigs: updateData.updateAppVersionConfig },
      });
    },
  });

  useEffect(() => {
    const sorted = data?.getAppVersionConfigs ? [...data.getAppVersionConfigs] : [];
    sorted.sort((a, b) => (a.version > b.version ? 0 : 1));
    setConfigs(sorted);
  }, [data?.getAppVersionConfigs]);

  const handleCreate = (version: string, type: string, platform: string) => {
    createConfig({
      variables: {
        version,
        forceUpdateType: type,
        platform,
      },
    })
      .then(({ data: createData }) => {
        setShowCreateModal(false);
        const sorted = createData?.createAppVersionConfig ? [...createData.createAppVersionConfig] : [];
        sorted.sort((a, b) => (a.version > b.version ? 0 : 1));
        setConfigs(sorted);
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  const handleDelete = () => {
    if (showDeleteModal) {
      deleteConfig({ variables: { version: showDeleteModal.version, platform: showDeleteModal.platform } }).then(
        ({ data: deleteData }) => {
          setShowDeleteModal(null);
          const sorted = deleteData?.deleteAppVersionConfig ? [...deleteData.deleteAppVersionConfig] : [];
          sorted.sort((a, b) => (a.version > b.version ? 0 : 1));
          setConfigs(sorted);
        }
      );
    }
  };

  const handleUpdate = (forceUpdateType: string) => {
    if (showEditModal) {
      updateConfig({
        variables: {
          version: showEditModal.version,
          platform: showEditModal.platform,
          forceUpdateType,
        },
      })
        .then(({ data: updateData }) => {
          setShowEditModal(null);
          const sorted = updateData?.updateAppVersionConfig ? [...updateData.updateAppVersionConfig] : [];
          sorted.sort((a, b) => (a.version > b.version ? 0 : 1));
          setConfigs(sorted);
        })
        .catch(() => {
          alert('Something went wrong :(');
        });
    }
  };


  return (
    <div className="d-flex flex-column">
      <CreateVersionConfigModal
        show={showCreateModal && !loading}
        loading={loading || createLoading}
        onHide={() => setShowCreateModal(false)}
        onAdd={handleCreate}
      />
      <DeleteVersionConfigModal
        config={showDeleteModal}
        show={!!showDeleteModal && !loading}
        onHide={() => setShowDeleteModal(null)}
        onConfirm={handleDelete}
      />
      <EditVersionConfigModal
        config={showEditModal}
        show={!!showEditModal && !loading}
        loading={loading || updateLoading}
        onHide={() => setShowEditModal(null)}
        onSave={handleUpdate}
      />

      <div className="d-flex">
        <Nav.Item className="mr-3">
          {loading ? (
            <Spinner animation="border" />
          ) : (
            <>
              <Button className="w-100" variant="success" onClick={() => setShowCreateModal(true)}>
                <i className="fas fa-plus mr-2" />
                Add NVS Campaign
              </Button>
            </>
          )}
        </Nav.Item>
        <div className="mx-auto">
          <Nav variant="pills" style={{ gap: '10px' }}>
            {configs.map((config) => (
              <div key={`${config.platform}-${config.version}`} className="d-flex">
                <Nav.Item className="w-100 mr-1 position-relative">
                  <Nav.Link
                    active
                    eventKey={config.version}
                    className="d-flex justify-content-between text-break align-items-center"
                  >
                    <span>{`${config.platform}: ${config.version}`}</span>
                    <Badge className="m-1" pill variant={config.forceUpdateType === 'mandatory' ? 'danger' : 'success'}>
                      {config.forceUpdateType}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Button className="mr-3" variant="secondary" onClick={() => setShowEditModal(config)}>
                  Edit
                </Button>
                <Button className="mr-3" variant="danger" onClick={() => setShowDeleteModal(config)}>
                  Delete
                </Button>
              </div>
            ))}
          </Nav>
        </div>
      </div>
    </div>
  );
};

export default VersionControl;
